import type {
  StockfishSearchOutcome,
  StockfishSearchRequest,
} from '../../engine';
import { selectStockfishMove } from '../stockfishSelection';
import type {
  AiRequestMetadata,
  GameSessionCommand,
  GameSessionState,
} from '../../session';

const UCI_PATTERN = /^[a-h][1-8][a-h][1-8][qrbn]?$/;

declare const validatedStockfishUci: unique symbol;

/** A UCI string that passed both syntax and the caller's current legality gate. */
export type ValidatedStockfishUci = string & {
  readonly [validatedStockfishUci]: true;
};

export interface StructuredStockfishEngineClient {
  search(request: StockfishSearchRequest): Promise<StockfishSearchOutcome>;
  cancel(searchId?: number): void;
}

export interface StockfishBridgeSearchLimits {
  readonly nodes?: number;
  readonly depth?: number;
  readonly moveTimeMs?: number;
  readonly multiPv?: number;
}

export interface StockfishBridgeRequestIdentity {
  readonly requestId: number;
  readonly gameEpoch: number;
  readonly positionRevision: number;
  /** Exact normalized six-field FEN; it is never shortened to board placement. */
  readonly fen: string;
}

export interface StockfishBridgeRequest extends StockfishBridgeRequestIdentity {
  readonly ply: number;
  readonly profileId: string;
  readonly tierVersion: string;
  readonly engineBuild: string;
  readonly gameSeed: string;
  readonly nodes?: number;
  readonly depth?: number;
  readonly moveTimeMs?: number;
  readonly multiPv?: number;
}

export interface StockfishBridgePositionIdentity {
  readonly gameEpoch: number;
  readonly positionRevision: number;
  readonly fen: string;
  readonly terminal: boolean;
}

export interface ValidateStockfishBridgeMoveInput {
  readonly uci: string;
  readonly request: StockfishBridgeRequest;
  readonly position: StockfishBridgePositionIdentity;
}

export type StockfishBridgeLegalMoves =
  | {
      readonly kind: 'set';
      readonly values: readonly string[] | ReadonlySet<string>;
    }
  | {
      readonly kind: 'validator';
      readonly validate: (input: ValidateStockfishBridgeMoveInput) => boolean;
    };

/**
 * Current caller-owned state. Reading it at completion time is what closes the
 * undo/load/new-game race; the engine's echoed metadata is not trusted alone.
 */
export interface StockfishBridgeLivePosition extends StockfishBridgePositionIdentity {
  readonly pendingRequest: StockfishBridgeRequestIdentity | null;
  readonly legalMoves: StockfishBridgeLegalMoves;
}

type ResolveAiCommand = Extract<GameSessionCommand, { readonly type: 'resolve-ai' }>;

export type StockfishBridgeMoveCommand = Omit<ResolveAiCommand, 'uci'> & {
  readonly uci: ValidatedStockfishUci;
};

export type StockfishBridgeFailureCommand = Omit<ResolveAiCommand, 'uci'> & {
  readonly uci: null;
};

export type StockfishBridgeFailureReason =
  | 'empty-bestmove'
  | 'engine-error'
  | 'illegal-bestmove'
  | 'malformed-bestmove'
  | 'mismatched-engine-result'
  | 'validator-error';

export type StockfishBridgeDiscardReason =
  | 'cancelled'
  | 'state-unavailable'
  | 'stale'
  | 'superseded'
  | 'terminal';

export type StockfishBridgeEffect =
  | {
      readonly type: 'dispatch-ai';
      readonly outcome: 'move';
      readonly searchId: number;
      readonly command: StockfishBridgeMoveCommand;
    }
  | {
      readonly type: 'dispatch-ai';
      readonly outcome: 'failure';
      readonly reason: StockfishBridgeFailureReason;
      readonly searchId: number;
      readonly command: StockfishBridgeFailureCommand;
      readonly message?: string;
    }
  | {
      readonly type: 'discard-ai';
      readonly reason: StockfishBridgeDiscardReason;
      readonly searchId: number | null;
      readonly request: StockfishBridgeRequestIdentity;
    };

export interface StockfishGameBridgeOptions {
  readonly engine: StructuredStockfishEngineClient;
  readonly readLivePosition: () => StockfishBridgeLivePosition;
  /** Injected so clock-sensitive GameSession commands remain deterministic in tests. */
  readonly now: () => number;
  /** Useful when the same engine client was already used before bridge creation. */
  readonly initialSearchId?: number;
}

interface Deferred<T> {
  readonly promise: Promise<T>;
  readonly resolve: (value: T) => void;
}

interface ActiveSearch {
  readonly request: StockfishBridgeRequest;
  readonly engineRequest: StockfishSearchRequest;
  readonly result: Promise<StockfishBridgeEffect>;
  readonly resolve: (effect: StockfishBridgeEffect) => void;
  settled: boolean;
  cancelledAs: 'cancelled' | 'superseded' | null;
}

type LiveCheck =
  | { readonly live: StockfishBridgeLivePosition }
  | { readonly effect: StockfishBridgeEffect };

/**
 * Converts GameSession's request metadata into the generic structured engine
 * request accepted by this bridge.
 */
export function createStockfishBridgeRequest(
  request: AiRequestMetadata,
  limits: StockfishBridgeSearchLimits,
): StockfishBridgeRequest {
  return Object.freeze({
    requestId: request.requestId,
    gameEpoch: request.gameEpoch,
    positionRevision: request.positionRevision,
    fen: request.positionFen,
    ply: request.ply,
    profileId: request.profileId,
    tierVersion: request.tierVersion,
    engineBuild: request.engineBuild,
    gameSeed: request.gameSeed,
    ...(limits.nodes === undefined ? {} : { nodes: limits.nodes }),
    ...(limits.depth === undefined ? {} : { depth: limits.depth }),
    ...(limits.moveTimeMs === undefined ? {} : { moveTimeMs: limits.moveTimeMs }),
    ...(limits.multiPv === undefined ? {} : { multiPv: limits.multiPv }),
  });
}

/** Creates the exact live value projection the bridge needs from GameSession. */
export function selectStockfishBridgeLivePosition(
  state: GameSessionState,
): StockfishBridgeLivePosition {
  const pending = state.ai.pending;
  const pendingRequest = pending === undefined
    ? null
    : Object.freeze({
        requestId: pending.requestId,
        gameEpoch: pending.gameEpoch,
        positionRevision: pending.positionRevision,
        fen: pending.positionFen,
      });

  return Object.freeze({
    gameEpoch: state.gameEpoch,
    positionRevision: state.position.revision,
    fen: state.position.fen,
    terminal: state.result.terminal,
    pendingRequest,
    legalMoves: Object.freeze({
      kind: 'set' as const,
      values: state.position.legalUciMoves,
    }),
  });
}

/**
 * Stateful only for request correlation. It owns no rules state and never
 * commits a move; callers dispatch only its `dispatch-ai` command back through
 * GameSession, which performs the final epoch/revision/FEN and RulesPort gate.
 */
export class StockfishGameBridge {
  private readonly engine: StructuredStockfishEngineClient;
  private readonly readLivePosition: () => StockfishBridgeLivePosition;
  private readonly now: () => number;
  private nextSearchId: number;
  private active: ActiveSearch | null = null;

  public constructor(options: StockfishGameBridgeOptions) {
    const initialSearchId = options.initialSearchId ?? 0;
    if (!Number.isSafeInteger(initialSearchId) || initialSearchId < 0) {
      throw new TypeError('initialSearchId must be a non-negative safe integer.');
    }

    this.engine = options.engine;
    this.readLivePosition = options.readLivePosition;
    this.now = options.now;
    this.nextSearchId = initialSearchId;
  }

  /** Starts one exact GameSession request, idempotently returning an in-flight duplicate. */
  public request(request: StockfishBridgeRequest): Promise<StockfishBridgeEffect> {
    const immutableRequest = freezeRequest(request);
    const preflight = this.checkLive(immutableRequest, null);
    if ('effect' in preflight) {
      return Promise.resolve(preflight.effect);
    }

    if (this.active && sameRequest(this.active.request, immutableRequest)) {
      return this.active.result;
    }

    this.supersedeActive();

    const searchId = this.nextSearchId + 1;
    this.nextSearchId = searchId;
    const engineRequest: StockfishSearchRequest = Object.freeze({
      searchId,
      gameEpoch: immutableRequest.gameEpoch,
      positionRevision: immutableRequest.positionRevision,
      fen: immutableRequest.fen,
      ...(immutableRequest.nodes === undefined ? {} : { nodes: immutableRequest.nodes }),
      ...(immutableRequest.depth === undefined ? {} : { depth: immutableRequest.depth }),
      ...(immutableRequest.moveTimeMs === undefined
        ? {}
        : { moveTimeMs: immutableRequest.moveTimeMs }),
      ...(immutableRequest.multiPv === undefined ? {} : { multiPv: immutableRequest.multiPv }),
    });
    const deferred = createDeferred<StockfishBridgeEffect>();
    const active: ActiveSearch = {
      request: immutableRequest,
      engineRequest,
      result: deferred.promise,
      resolve: deferred.resolve,
      settled: false,
      cancelledAs: null,
    };
    this.active = active;

    let engineResult: Promise<StockfishSearchOutcome>;
    try {
      engineResult = this.engine.search(engineRequest);
    } catch (error) {
      this.finishError(active, error);
      return active.result;
    }

    void Promise.resolve(engineResult).then(
      (outcome) => this.finishOutcome(active, outcome),
      (error: unknown) => this.finishError(active, error),
    );
    return active.result;
  }

  /**
   * Cancels only the exact active GameSession request. A mismatched late cancel
   * cannot stop newer work, and local settlement does not rely on engine cooperation.
   */
  public cancel(request: StockfishBridgeRequestIdentity): boolean {
    const active = this.active;
    if (!active || !sameRequest(active.request, request)) {
      return false;
    }

    if (!active.settled) {
      active.cancelledAs = 'cancelled';
      this.safeEngineCancel(active.engineRequest.searchId);
      this.settle(active, this.discard(active.request, 'cancelled', active.engineRequest.searchId));
    }
    return true;
  }

  private supersedeActive(): void {
    const active = this.active;
    if (!active || active.settled) {
      return;
    }

    active.cancelledAs = 'superseded';
    this.safeEngineCancel(active.engineRequest.searchId);
    this.settle(active, this.discard(active.request, 'superseded', active.engineRequest.searchId));
  }

  private safeEngineCancel(searchId: number): void {
    try {
      this.engine.cancel(searchId);
    } catch {
      // The local request is already terminal. A broken engine cancel must not
      // make its eventual result dispatchable again.
    }
  }

  private finishOutcome(active: ActiveSearch, outcome: StockfishSearchOutcome): void {
    if (active.settled) {
      return;
    }
    if (active.cancelledAs) {
      this.settle(
        active,
        this.discard(active.request, active.cancelledAs, active.engineRequest.searchId),
      );
      return;
    }

    const liveCheck = this.checkLive(active.request, active.engineRequest.searchId);
    if ('effect' in liveCheck) {
      this.settle(active, liveCheck.effect);
      return;
    }

    if (!matchesEngineRequest(outcome, active.engineRequest)) {
      this.settle(
        active,
        this.failure(active, 'mismatched-engine-result', 'Engine result metadata did not match its request.'),
      );
      return;
    }

    if (outcome.status === 'cancelled') {
      this.settle(active, this.discard(active.request, 'cancelled', active.engineRequest.searchId));
      return;
    }

    const rawBestMove = outcome.bestMove;
    if (rawBestMove === null) {
      this.settle(active, this.failure(active, 'empty-bestmove'));
      return;
    }
    if (!UCI_PATTERN.test(rawBestMove)) {
      this.settle(active, this.failure(active, 'malformed-bestmove'));
      return;
    }

    let rawBestMoveLegal: boolean;
    try {
      rawBestMoveLegal = validatesLegalMove(liveCheck.live, active.request, rawBestMove);
    } catch (error) {
      this.settle(active, this.failure(active, 'validator-error', messageFor(error)));
      return;
    }
    if (!rawBestMoveLegal) {
      this.settle(active, this.failure(active, 'illegal-bestmove'));
      return;
    }

    // MultiPV 1 is the uncompromised strength contract: always use Stockfish's
    // terminal bestmove. Higher MultiPV tiers may deliberately sample a nearby
    // line to create varied, learner-friendly play.
    const selectedMove = active.request.multiPv === 1
      ? rawBestMove
      : liveCheck.live.legalMoves.kind === 'set'
      ? selectStockfishMove({
          bestMove: rawBestMove,
          principalVariations: outcome.principalVariations,
          legalMoves: liveCheck.live.legalMoves.values,
          gameSeed: active.request.gameSeed,
          tierVersion: active.request.tierVersion,
          engineBuild: active.request.engineBuild,
          positionFen: active.request.fen,
          ply: active.request.ply,
        }).uci
      : rawBestMove;
    if (selectedMove === null) {
      this.settle(active, this.failure(active, 'illegal-bestmove'));
      return;
    }

    let selectedMoveLegal: boolean;
    try {
      selectedMoveLegal = validatesLegalMove(liveCheck.live, active.request, selectedMove);
    } catch (error) {
      this.settle(active, this.failure(active, 'validator-error', messageFor(error)));
      return;
    }
    if (!selectedMoveLegal) {
      this.settle(active, this.failure(active, 'illegal-bestmove'));
      return;
    }

    const command: StockfishBridgeMoveCommand = Object.freeze({
      ...resolveCommandBase(active.request, this.now()),
      uci: selectedMove as ValidatedStockfishUci,
    });
    this.settle(active, Object.freeze({
      type: 'dispatch-ai',
      outcome: 'move',
      searchId: active.engineRequest.searchId,
      command,
    }));
  }

  private finishError(active: ActiveSearch, error: unknown): void {
    if (active.settled) {
      return;
    }
    if (active.cancelledAs) {
      this.settle(
        active,
        this.discard(active.request, active.cancelledAs, active.engineRequest.searchId),
      );
      return;
    }

    const liveCheck = this.checkLive(active.request, active.engineRequest.searchId);
    if ('effect' in liveCheck) {
      this.settle(active, liveCheck.effect);
      return;
    }
    this.settle(active, this.failure(active, 'engine-error', messageFor(error)));
  }

  private failure(
    active: ActiveSearch,
    reason: StockfishBridgeFailureReason,
    message?: string,
  ): StockfishBridgeEffect {
    const command: StockfishBridgeFailureCommand = Object.freeze({
      ...resolveCommandBase(active.request, this.now()),
      uci: null,
    });
    return Object.freeze({
      type: 'dispatch-ai',
      outcome: 'failure',
      reason,
      searchId: active.engineRequest.searchId,
      command,
      ...(message === undefined ? {} : { message }),
    });
  }

  private checkLive(
    request: StockfishBridgeRequestIdentity,
    searchId: number | null,
  ): LiveCheck {
    let live: StockfishBridgeLivePosition;
    try {
      live = this.readLivePosition();
    } catch {
      return { effect: this.discard(request, 'state-unavailable', searchId) };
    }

    if (
      live.gameEpoch !== request.gameEpoch ||
      live.positionRevision !== request.positionRevision ||
      live.fen !== request.fen
    ) {
      return { effect: this.discard(request, 'stale', searchId) };
    }
    if (live.terminal) {
      return { effect: this.discard(request, 'terminal', searchId) };
    }
    if (live.pendingRequest === null || !sameRequest(live.pendingRequest, request)) {
      return { effect: this.discard(request, 'stale', searchId) };
    }
    return { live };
  }

  private discard(
    request: StockfishBridgeRequestIdentity,
    reason: StockfishBridgeDiscardReason,
    searchId: number | null,
  ): StockfishBridgeEffect {
    return Object.freeze({
      type: 'discard-ai',
      reason,
      searchId,
      request: freezeIdentity(request),
    });
  }

  private settle(active: ActiveSearch, effect: StockfishBridgeEffect): void {
    if (active.settled) {
      return;
    }
    active.settled = true;
    active.resolve(effect);
  }
}

function resolveCommandBase(
  request: StockfishBridgeRequestIdentity,
  atMs: number,
): Omit<ResolveAiCommand, 'uci'> {
  return {
    type: 'resolve-ai',
    requestId: request.requestId,
    gameEpoch: request.gameEpoch,
    positionRevision: request.positionRevision,
    positionFen: request.fen,
    atMs,
  };
}

function validatesLegalMove(
  live: StockfishBridgeLivePosition,
  request: StockfishBridgeRequest,
  uci: string,
): boolean {
  if (live.legalMoves.kind === 'validator') {
    return live.legalMoves.validate({
      uci,
      request,
      position: {
        gameEpoch: live.gameEpoch,
        positionRevision: live.positionRevision,
        fen: live.fen,
        terminal: live.terminal,
      },
    });
  }

  const values = live.legalMoves.values;
  return Array.isArray(values)
    ? values.includes(uci)
    : (values as ReadonlySet<string>).has(uci);
}

function matchesEngineRequest(
  outcome: StockfishSearchOutcome,
  request: StockfishSearchRequest,
): boolean {
  return (
    outcome !== null &&
    typeof outcome === 'object' &&
    outcome.searchId === request.searchId &&
    outcome.gameEpoch === request.gameEpoch &&
    outcome.positionRevision === request.positionRevision &&
    outcome.fen === request.fen &&
    (outcome.status === 'bestmove' || outcome.status === 'cancelled')
  );
}

function sameRequest(
  left: StockfishBridgeRequestIdentity,
  right: StockfishBridgeRequestIdentity,
): boolean {
  return (
    left.requestId === right.requestId &&
    left.gameEpoch === right.gameEpoch &&
    left.positionRevision === right.positionRevision &&
    left.fen === right.fen
  );
}

function freezeRequest(request: StockfishBridgeRequest): StockfishBridgeRequest {
  return Object.freeze({
    requestId: request.requestId,
    gameEpoch: request.gameEpoch,
    positionRevision: request.positionRevision,
    fen: request.fen,
    ply: request.ply,
    profileId: request.profileId,
    tierVersion: request.tierVersion,
    engineBuild: request.engineBuild,
    gameSeed: request.gameSeed,
    ...(request.nodes === undefined ? {} : { nodes: request.nodes }),
    ...(request.depth === undefined ? {} : { depth: request.depth }),
    ...(request.moveTimeMs === undefined ? {} : { moveTimeMs: request.moveTimeMs }),
    ...(request.multiPv === undefined ? {} : { multiPv: request.multiPv }),
  });
}

function freezeIdentity(
  request: StockfishBridgeRequestIdentity,
): StockfishBridgeRequestIdentity {
  return Object.freeze({
    requestId: request.requestId,
    gameEpoch: request.gameEpoch,
    positionRevision: request.positionRevision,
    fen: request.fen,
  });
}

function createDeferred<T>(): Deferred<T> {
  let resolve!: (value: T) => void;
  const promise = new Promise<T>((promiseResolve) => {
    resolve = promiseResolve;
  });
  return { promise, resolve };
}

function messageFor(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
